import React, { useState } from 'react';
import {
  BarChart3, Euro, Users, FileText, CheckCircle, Clock, User,
  Calendar, TrendingUp, Building2
} from 'lucide-react';
import {
  getFilteredAffaires, getFilteredDevis,
  getIngenieurById, getSocieteById, getAgenceById,
  ingenieurs
} from '../data/mockData';
import { useFilters } from '../App';

const Statistiques = () => {
  const filters = useFilters();
  
  // Période par défaut : année calendaire actuelle
  const currentYear = new Date().getFullYear();
  const [dateDebut, setDateDebut] = useState(`${currentYear}-01-01`);
  const [dateFin, setDateFin] = useState(`${currentYear}-12-31`);

  const allAffaires = getFilteredAffaires(filters);
  const allDevis = getFilteredDevis(filters);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  // Filtrer par période
  const isInPeriod = (dateStr) => {
    if (!dateStr) return false;
    return dateStr >= dateDebut && dateStr <= dateFin;
  };

  // Production totale sur la période
  const productionTotale = allAffaires
    .filter(a => isInPeriod(a.dateCreation))
    .reduce((acc, a) => acc + a.montantHT, 0);

  // Production par ingénieur sur la période
  const filteredIngenieurs = filters.agenceId
    ? ingenieurs.filter(i => i.agenceId === filters.agenceId)
    : ingenieurs;

  const productionParIngenieur = filteredIngenieurs.map(ing => {
    const affairesIng = allAffaires.filter(a => 
      a.ingenieurId === ing.id && isInPeriod(a.dateCreation)
    );
    const total = affairesIng.reduce((acc, a) => acc + a.montantHT, 0);
    const propre = affairesIng.reduce((acc, a) => 
      acc + (a.montantHT - a.montantSousTraitanceExterne - a.montantSousTraitanceInterne), 0
    );
    return {
      ingenieur: ing,
      total,
      propre,
      nbAffaires: affairesIng.length
    };
  }).filter(p => p.total > 0);

  // Devis en attente sur la période
  const devisEnAttente = allDevis
    .filter(d => d.statut === 'envoye' && isInPeriod(d.dateCreation))
    .reduce((acc, d) => acc + d.montantHT, 0);

  // Devis validés sur la période
  const devisValides = allDevis
    .filter(d => d.statut === 'accepte' && isInPeriod(d.dateCreation))
    .reduce((acc, d) => acc + d.montantHT, 0);

  // Sous-traitance sur la période
  const montantSousTraite = allAffaires
    .filter(a => isInPeriod(a.dateCreation))
    .reduce((acc, a) => acc + a.montantSousTraitanceExterne, 0);

  const activeSociete = filters.societeId ? getSocieteById(filters.societeId) : null;
  const activeAgence = filters.agenceId ? getAgenceById(filters.agenceId) : null;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><BarChart3 size={28} /> Statistiques</h1>
          <div className="page-meta">
            <span>
              <Calendar size={16} /> 
              {new Date(dateDebut).toLocaleDateString('fr-FR')} - {new Date(dateFin).toLocaleDateString('fr-FR')}
            </span>
            {activeSociete && <span><Building2 size={16} /> {activeSociete.nom}</span>}
            {activeAgence && <span>Agence {activeAgence.nom}</span>}
          </div>
        </div>
      </div>

      {/* Sélection de période */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        padding: '1.5rem', marginBottom: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar size={18} /> Période d'analyse
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', maxWidth: '600px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
              Date de début
            </label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              style={{ fontSize: '0.9375rem', width: '100%' }}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
              Date de fin
            </label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              style={{ fontSize: '0.9375rem', width: '100%' }}
            />
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => {
              const year = new Date().getFullYear();
              setDateDebut(`${year}-01-01`);
              setDateFin(`${year}-12-31`);
            }}
            className="btn btn-sm btn-secondary"
          >
            Année actuelle
          </button>
          <button
            onClick={() => {
              const year = new Date().getFullYear() - 1;
              setDateDebut(`${year}-01-01`);
              setDateFin(`${year}-12-31`);
            }}
            className="btn btn-sm btn-secondary"
          >
            Année précédente
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setDateDebut(firstDay.toISOString().split('T')[0]);
              setDateFin(today.toISOString().split('T')[0]);
            }}
            className="btn btn-sm btn-secondary"
          >
            Mois en cours
          </button>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '1.5rem' }}>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Euro size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Production totale</p>
            <p className="stat-value">{formatMontant(productionTotale)}</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><Clock size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Devis en attente</p>
            <p className="stat-value">{formatMontant(devisEnAttente)}</p>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Devis validés</p>
            <p className="stat-value">{formatMontant(devisValides)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#EDE9FE', color: '#7C3AED' }}><Users size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Sous-traitance</p>
            <p className="stat-value">{formatMontant(montantSousTraite)}</p>
          </div>
        </div>
      </div>

      {/* Production par ingénieur */}
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-200)',
        padding: '1.5rem', marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={18} /> Production par ingénieur
          </h3>
          <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
            {productionParIngenieur.length} ingénieur{productionParIngenieur.length > 1 ? 's' : ''}
          </span>
        </div>
        {productionParIngenieur.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {productionParIngenieur
              .sort((a, b) => b.total - a.total)
              .map(({ ingenieur, total, propre, nbAffaires }) => (
                <div key={ingenieur.id} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                  border: '1px solid var(--gray-200)'
                }}>
                  <div className="user-avatar" style={{ background: ingenieur.couleur, flexShrink: 0 }}>
                    {ingenieur.initiales}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{ingenieur.prenom} {ingenieur.nom}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginLeft: '0.5rem' }}>{ingenieur.role}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.125rem' }}>
                          {formatMontant(total)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {nbAffaires} affaire{nbAffaires > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                      <span>Production propre : <strong>{formatMontant(propre)}</strong></span>
                      <span>
                        {total > 0 && (
                          <span style={{ color: '#059669', fontWeight: 600 }}>
                            {Math.round((propre / total) * 100)}% propre
                          </span>
                        )}
                      </span>
                    </div>
                    {/* Barre de progression */}
                    <div className="progress-bar" style={{ width: '100%', height: '6px', marginTop: '0.5rem' }}>
                      <div
                        className="progress-fill"
                        style={{
                          width: `${productionTotale > 0 ? (total / productionTotale) * 100 : 0}%`,
                          background: ingenieur.couleur
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}>
            <User size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
            <p>Aucune production sur cette période</p>
          </div>
        )}
      </div>

      {/* Résumé */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary-50) 0%, #EFF6FF 100%)',
        borderRadius: 'var(--radius-lg)', border: '2px solid var(--primary-100)',
        padding: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BarChart3 size={18} /> Résumé de la période
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>
              Production totale
            </p>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '1.5rem', color: 'var(--primary)' }}>
              {formatMontant(productionTotale)}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>
              Production propre
            </p>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '1.5rem', color: '#059669' }}>
              {formatMontant(productionTotale - montantSousTraite)}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>
              Devis en attente
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem' }}>
              {formatMontant(devisEnAttente)}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', margin: '0 0 0.25rem' }}>
              Devis validés
            </p>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem', color: '#059669' }}>
              {formatMontant(devisValides)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistiques;
